import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Plus, Trash2, Send, Save, Clock } from "lucide-react";
import { toast } from "sonner";

export function DailyReportSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/reports/daily-settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  if (!s) return <Card className="p-6 bg-card border-border">Loading…</Card>;

  const nums = s.numbers || [];
  const setNum = (i, patch) => setS({ ...s, numbers: nums.map((n, idx) => idx === i ? { ...n, ...patch } : n) });
  const addNum = () => setS({ ...s, numbers: [...nums, { number: "", enabled: true }] });
  const delNum = (i) => setS({ ...s, numbers: nums.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/reports/daily-settings", s); setS(data); toast.success("Daily report settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/reports/daily-test");
      if (data.ok) toast.success(`Test report sent to ${data.sent_to.length} number(s)`);
      else toast.error(data.message || "Add a number first");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="daily-report-settings">
      <Card className="p-6 bg-card border-border space-y-5">
        <div>
          <h3 className="font-display text-xl">Daily WhatsApp Business Report</h3>
          <p className="text-xs text-muted-foreground mt-1">Auto-sends branch-wise business, expense, balance and payment breakup to the Owner's WhatsApp at your chosen time (IST). Amounts come live from Billing & Expenses.</p>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm">Daily Report</span>
          <Switch checked={!!s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="dr-enabled" />
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-xs flex items-center gap-1"><Clock size={13} /> Send Time (IST)</Label>
          <Input type="time" value={s.send_time} onChange={(e) => setS({ ...s, send_time: e.target.value })} className="bg-secondary w-40" data-testid="dr-send-time" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Owner WhatsApp Numbers</Label>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={addNum} data-testid="dr-add-number"><Plus size={14} className="mr-1" /> Add Number</Button>
          </div>
          {nums.length === 0 && <p className="text-xs text-muted-foreground">No numbers yet — add at least one to receive the report.</p>}
          <div className="space-y-2">
            {nums.map((n, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`dr-number-${i}`}>
                <Input value={n.number} onChange={(e) => setNum(i, { number: e.target.value })} placeholder="e.g. 9198XXXXXXXX" className="bg-secondary flex-1" data-testid={`dr-number-input-${i}`} />
                <div className="flex items-center gap-1"><Switch checked={n.enabled !== false} onCheckedChange={(v) => setNum(i, { enabled: v })} data-testid={`dr-number-enabled-${i}`} /><span className="text-[11px] text-muted-foreground w-8">{n.enabled !== false ? "On" : "Off"}</span></div>
                <Button size="icon" variant="ghost" onClick={() => delNum(i)} data-testid={`dr-number-del-${i}`}><Trash2 size={15} className="text-destructive" /></Button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={save} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="dr-save"><Save size={15} className="mr-1" /> {saving ? "Saving…" : "Save Settings"}</Button>
          <Button variant="outline" onClick={sendTest} disabled={testing} className="rounded-sm" data-testid="dr-send-test"><Send size={15} className="mr-1" /> {testing ? "Sending…" : "Send Test Report"}</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">You can change the send time anytime — no restart needed. Only one automatic report is sent per number per day.</p>
      </Card>
    </div>
  );
}
